import { FC, ReactNode } from 'react'
import styled from '@emotion/styled'
import colors from '../colors'

type SvgProps = {
	value: number
	thickness: number
	color: string
}

const Progress = styled.div<{ size: number }>`
	position: relative;
	width: ${p => p.size}px;
	height: ${p => p.size}px;
`

const ProgressCircle = styled.svg<SvgProps & { size: number }>`
	height: ${p => p.size}px;
	transform: rotate(-90deg);
	width: ${p => p.size}px;

	circle {
		fill: none;
		stroke-width: ${p => p.thickness};
		stroke: ${p => p.color};

		&:nth-of-type(1) {
			stroke-dashoffset: 0;
			stroke: ${colors.brightGray};
		}

		&:nth-of-type(2) {
			stroke: ${p => p.color};
			stroke-dasharray: 100 100;
			stroke-dashoffset: ${props => props.value};
			stroke-linecap: round;
			transition: stroke-dashoffset 1s ease-in-out;
		}
	}
`

const ProgressText = styled.p`
	color: ${colors.black};
	font-size: 14px;
	font-weight: 700;
	left: 50%;
	margin: 0;
	position: absolute;
	top: 50%;
	transform: translate(-50%, -50%);
`

const calculatePercentage = (currentValue: number, maxValue: number) => {
	return 100 - (100 * (currentValue / maxValue) * 100) / 100
}

type Props = {
	value: number
	maxValue: number
	/** In pixels. */
	size?: number
	/** The ring's width, in the units of its 32-wide circle. */
	thickness?: number
	color?: string
	/** In the middle; "value/maxValue" by default. */
	children?: ReactNode
}

export const CircleProgress: FC<Props> = ({
	value,
	maxValue,
	size = 50,
	thickness = 2,
	color = colors.greenishCyan,
	children,
}) => (
	<Progress size={size}>
		<ProgressCircle
			value={calculatePercentage(value, maxValue)}
			size={size}
			thickness={thickness}
			color={color}
			viewBox={`${-thickness / 2} ${-thickness / 2} ${32 + thickness} ${
				32 + thickness
			}`}
		>
			<circle cx="16" cy="16" r="15.9155" />
			<circle cx="16" cy="16" r="15.9155" />
		</ProgressCircle>
		<ProgressText>
			{children ?? (
				<>
					{value}/{maxValue}
				</>
			)}
		</ProgressText>
	</Progress>
)

export default CircleProgress
